import * as Sentry from "@sentry/nextjs";

type CaptureContext = NonNullable<Parameters<typeof Sentry.captureException>[1]>;

type Tags = Record<string, string | number | boolean>;
type Extra = Record<string, unknown>;

export interface UserVisibleErrorOptions {
  /**
   * Short identifier for the surface where the error became visible — e.g.
   * "chat_room_leave", "workout_player", "app_home". Used as the Sentry tag
   * `surface` so events are easy to group and search.
   */
  surface: string;
  /** Extra Sentry tags. */
  tags?: Tags;
  /** Extra context. Stack-style data goes here. */
  extra?: Extra;
  /** Optional message used when `error` is not an Error instance. */
  message?: string;
}

/**
 * Capture an error that the user actually saw — a toast, error screen, or
 * other visible failure state. Tags the event with `user_visible: true` so it
 * bypasses the global beforeSend network-error filter (see
 * scripts/sentry/error-filter.ts). If you saw it, we want it in Sentry.
 *
 * One exception, decided ahead of the bypass: a response-less failure thrown
 * while the page is unloading is dropped, because a read the browser cancelled
 * on navigation showed the user nothing.
 */
export function captureUserVisibleError(
  error: unknown,
  options: UserVisibleErrorOptions
): void {
  const context: CaptureContext = {
    tags: {
      user_visible: true,
      surface: options.surface,
      ...options.tags,
    },
    extra: options.extra,
  };

  if (error instanceof Error) {
    Sentry.captureException(error, context);
    return;
  }

  // Non-Error thrown value — wrap it so we get a stack at the capture site.
  const wrapped = new Error(
    options.message ?? `[${options.surface}] non-Error thrown value`
  );
  Sentry.captureException(wrapped, {
    ...context,
    extra: { ...options.extra, originalError: error },
  });
}
